
import React from 'react';
import { Calendar, Clock } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { CalendarIcon } from '@radix-ui/react-icons';
import { format } from 'date-fns';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import TimeSelector from './TimeSelector';

interface DateTimeFormFieldsProps {
  startDate: Date;
  setStartDate: (value: Date) => void;
  startTime: string;
  setStartTime: (value: string) => void;
  endDate: Date | undefined;
  setEndDate: (value: Date | undefined) => void;
  endTime: string;
  setEndTime: (value: string) => void;
  isRecurring: boolean;
  setIsRecurring: (value: boolean) => void;
}

const DateTimeFormFields: React.FC<DateTimeFormFieldsProps> = ({
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  endDate,
  setEndDate,
  endTime,
  setEndTime,
  isRecurring,
  setIsRecurring,
}) => {
  const handleEndDateChange = (date: Date | undefined) => {
    setEndDate(date);
    setIsRecurring(!!date);
  };

  return (
    <div className="space-y-4">
      {/* Start Date and Time */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="startDate" className="text-sm font-medium">
            <Calendar className="inline-block h-4 w-4 mr-2" />
            Start Date
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {startDate ? format(startDate, "PPP") : <span>Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <CalendarComponent 
                mode="single" 
                selected={startDate} 
                onSelect={(date) => date && setStartDate(date)} 
              />
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <Label htmlFor="startTime" className="text-sm font-medium">
            <Clock className="inline-block h-4 w-4 mr-2" />
            Start Time
          </Label>
          <TimeSelector
            label=""
            value={startTime}
            onChange={setStartTime}
          />
        </div>
      </div>

      {/* End Date and Time */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="endDate" className="text-sm font-medium">
            <Calendar className="inline-block h-4 w-4 mr-2" />
            End Date (Optional)
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-full justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {endDate ? format(endDate, "PPP") : <span>Same day</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <div className="p-3">
                <CalendarComponent 
                  mode="single" 
                  selected={endDate} 
                  onSelect={handleEndDateChange}
                  disabled={(date) => date < startDate}
                />
                {endDate && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full mt-2"
                    onClick={() => handleEndDateChange(undefined)}
                  >
                    Clear End Date
                  </Button>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </div>

        <div>
          <Label htmlFor="endTime" className="text-sm font-medium">
            <Clock className="inline-block h-4 w-4 mr-2" />
            End Time
          </Label>
          <TimeSelector
            label=""
            value={endTime}
            onChange={setEndTime}
          />
        </div>
      </div>

      {/* Recurring Schedule Info */}
      {isRecurring && endDate && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800">
            <strong>Recurring Schedule:</strong> This will create schedule items from{' '}
            {format(startDate, 'MMM d')} to {format(endDate, 'MMM d, yyyy')}.
          </p>
          <ul className="text-xs text-blue-600 mt-2 space-y-1">
            <li>• First day: {startTime} - end of work day</li>
            {startDate.toDateString() !== endDate.toDateString() && (
              <>
                <li>• Middle days: Full work day (8:00 AM - 8:00 PM)</li>
                <li>• Last day: Start of work day - {endTime}</li>
              </>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default DateTimeFormFields;
